import * as React from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import {
  normalizeTastingNotes,
  shouldOfferCreateTastingNote,
} from "./product-tasting-notes";

interface TastingNotesSelectProps {
  value: string[];
  options: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  loading?: boolean;
}

export function TastingNotesSelect({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  loading = false,
}: TastingNotesSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  const normalizedValue = React.useMemo(() => normalizeTastingNotes(value), [value]);
  const normalizedOptions = React.useMemo(() => normalizeTastingNotes(options), [options]);
  const selectedSet = React.useMemo(
    () => new Set(normalizedValue.map((item) => item.toLowerCase())),
    [normalizedValue],
  );

  const filteredOptions = React.useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return normalizedOptions;
    }

    return normalizedOptions.filter((option) => option.toLowerCase().includes(query));
  }, [normalizedOptions, searchValue]);

  const normalizedSearch = React.useMemo(
    () => normalizeTastingNotes(searchValue ? [searchValue] : [])[0] ?? "",
    [searchValue],
  );
  const canCreate = React.useMemo(
    () => shouldOfferCreateTastingNote({
      searchValue,
      selectedValues: normalizedValue,
      options: normalizedOptions,
    }),
    [normalizedOptions, normalizedValue, searchValue],
  );

  const handleChange = React.useCallback(
    (nextValue: string[]) => {
      onChange(normalizeTastingNotes(nextValue));
    },
    [onChange],
  );

  const toggleValue = React.useCallback(
    (item: string) => {
      const itemKey = item.toLowerCase();
      if (selectedSet.has(itemKey)) {
        handleChange(normalizedValue.filter((valueItem) => valueItem.toLowerCase() !== itemKey));
        return;
      }

      handleChange([...normalizedValue, item]);
    },
    [handleChange, normalizedValue, selectedSet],
  );

  const addSearchValue = React.useCallback(() => {
    if (!normalizedSearch) {
      return;
    }

    handleChange([...normalizedValue, normalizedSearch]);
    setSearchValue("");
  }, [handleChange, normalizedSearch, normalizedValue]);

  const summary = normalizedValue.length > 0
    ? normalizedValue.join(", ")
    : placeholder;

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <span className={cn("truncate text-left", normalizedValue.length === 0 && "text-muted-foreground")}>
              {summary}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              {loading ? (
                <CommandEmpty>Loading...</CommandEmpty>
              ) : filteredOptions.length === 0 && !canCreate ? (
                <CommandEmpty>{emptyText}</CommandEmpty>
              ) : (
                <>
                  {filteredOptions.length > 0 ? (
                    <CommandGroup>
                      {filteredOptions.map((option) => {
                        const isSelected = selectedSet.has(option.toLowerCase());
                        return (
                          <CommandItem
                            key={option}
                            value={option}
                            onSelect={() => toggleValue(option)}
                          >
                            <Check className={cn("mr-2 h-4 w-4", !isSelected && "text-transparent")} />
                            <span>{option}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  ) : null}
                  {canCreate ? (
                    <>
                      {filteredOptions.length > 0 ? <CommandSeparator /> : null}
                      <CommandGroup>
                        <CommandItem value={`add-${normalizedSearch}`} onSelect={addSearchValue}>
                          <Plus className="mr-2 h-4 w-4" />
                          <span>Add "{normalizedSearch}"</span>
                        </CommandItem>
                      </CommandGroup>
                    </>
                  ) : null}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {normalizedValue.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {normalizedValue.map((item) => (
            <Badge key={item} variant="secondary" className="gap-1 pr-1">
              <span>{item}</span>
              <button
                type="button"
                className="rounded-sm p-0.5 hover:bg-black/5"
                onClick={() => toggleValue(item)}
                aria-label={`Remove ${item}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}
